import { getRangeIds, isMediaSelectable, type MediaItem } from './mediaItems';

// A tiny external store for the media library's multi-select. Tiles subscribe
// with useSyncExternalStore to just their own `isSelected(id)`, so toggling
// one tile re-renders that tile and the action bar — not the whole grid.

export interface MediaSelectionStore {
  subscribe: (listener: () => void) => () => void;
  isSelected: (id: number) => boolean;
  /** Selection mode: something is selected, or the user pressed "Select". */
  isActive: () => boolean;
  getCount: () => number;
  /** Stable between changes (safe as a useSyncExternalStore snapshot). */
  getItems: () => MediaItem[];
  /** Click: flip one item. Shift-click: select everything from the anchor. */
  toggle: (item: MediaItem, order: MediaItem[], range?: boolean) => void;
  setMany: (items: MediaItem[], selected: boolean) => void;
  /** Replace the selection with exactly these items (keeps selection mode). */
  replace: (items: MediaItem[]) => void;
  clear: () => void;
  setExplicitMode: (on: boolean) => void;
  removeIds: (ids: number[]) => void;
  patchItems: (ids: number[], patch: Partial<MediaItem>) => void;
}

export const createMediaSelectionStore = (): MediaSelectionStore => {
  let selected = new Map<number, MediaItem>();
  let explicitMode = false;
  let anchorId: number | null = null;
  let snapshot: MediaItem[] = [];
  const listeners = new Set<() => void>();

  const emit = () => {
    snapshot = Array.from(selected.values());
    listeners.forEach((listener) => listener());
  };

  const commit = (next: Map<number, MediaItem>) => {
    selected = next;
    emit();
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    isSelected: (id) => selected.has(id),
    isActive: () => explicitMode || selected.size > 0,
    getCount: () => selected.size,
    getItems: () => snapshot,

    toggle(item, order, range = false) {
      if (typeof item.id !== 'number' || !isMediaSelectable(item)) return;
      const next = new Map(selected);
      if (range && anchorId !== null && anchorId !== item.id) {
        const selectable = order.filter((entry) => isMediaSelectable(entry));
        const byId = new Map(
          selectable.map((entry) => [entry.id as number, entry]),
        );
        const ids = getRangeIds(
          selectable.map((entry) => entry.id as number),
          anchorId,
          item.id,
        );
        ids.forEach((id) => {
          const entry = byId.get(id);
          if (entry) next.set(id, entry);
        });
        // The range end becomes the next anchor, like Finder and Photos.
        anchorId = item.id;
        commit(next);
        return;
      }
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      anchorId = item.id;
      commit(next);
    },

    setMany(items, value) {
      const next = new Map(selected);
      items.forEach((item) => {
        if (typeof item.id !== 'number') return;
        if (value) {
          if (isMediaSelectable(item)) next.set(item.id, item);
        } else next.delete(item.id);
      });
      commit(next);
    },

    replace(items) {
      const next = new Map<number, MediaItem>();
      items.forEach((item) => {
        if (typeof item.id === 'number' && isMediaSelectable(item))
          next.set(item.id, item);
      });
      anchorId = null;
      commit(next);
    },

    clear() {
      explicitMode = false;
      anchorId = null;
      commit(new Map());
    },

    setExplicitMode(on) {
      explicitMode = on;
      if (!on) {
        anchorId = null;
        selected = new Map();
      }
      emit();
    },

    removeIds(ids) {
      if (!ids.length) return;
      const next = new Map(selected);
      ids.forEach((id) => next.delete(id));
      if (anchorId !== null && ids.includes(anchorId)) anchorId = null;
      commit(next);
    },

    patchItems(ids, patch) {
      let changed = false;
      const next = new Map(selected);
      ids.forEach((id) => {
        const current = next.get(id);
        if (current) {
          next.set(id, { ...current, ...patch });
          changed = true;
        }
      });
      if (changed) commit(next);
    },
  };
};
